import { useEffect } from "react";
import { Link, useSearchParams } from "wouter";
import { scrollToPageTop } from "@/components/HelioLogoMark";
import Seo from "@/components/Seo";
import PowloLayout from "@/components/powlo/PowloLayout";
import PostCard from "@/components/Blog/PostCard";
import TagChip from "@/components/Blog/TagChip";
import { useBlogPosts } from "@/api/hooks";

function parsePage(value: string | null): number {
  const page = Number(value || "1");
  return Number.isFinite(page) && page > 0 ? page : 1;
}

export default function BlogIndex() {
  const [searchParams] = useSearchParams();
  const tag = searchParams.get("tag") ?? "";
  const page = parsePage(searchParams.get("page"));
  const { data, isLoading, isError, refetch } = useBlogPosts({ page, tag: tag || undefined });

  useEffect(() => {
    scrollToPageTop();
  }, [page, tag]);

  const tags = Array.from(new Set((data?.data ?? []).flatMap((post) => post.tags)));

  return (
    <>
      <Seo
        title="Blog"
        description="Energy, policy, and what we're building in Brighton."
        path="/blog"
        ogType="website"
      />
      <PowloLayout>
        <section className="flex scroll-mt-36 flex-col gap-10 px-5 pb-24 sm:px-8 lg:px-20">
          <p className="text-[13px] tracking-[0.14em] uppercase text-[#666666] mb-4">Powlo</p>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-[3.4rem] font-medium tracking-[-0.01em] leading-[1.08] mb-5 text-brand-navy">
            Insights
          </h1>
          <p className="text-lg sm:text-xl text-[#666666] leading-relaxed">
            Energy, policy, and what we&apos;re building in Brighton.
          </p>
        </section>
        <section className="flex scroll-mt-36 flex-col gap-10 px-5 pb-24 sm:px-8 lg:px-20">
          {tags.length > 0 ? (
            <div className="flex flex-wrap gap-2 mb-12">
              {tags.map((item) => (
                <TagChip key={item} tag={item} active={tag === item} />
              ))}
            </div>
          ) : null}

          {isLoading ? <p className="text-[#666666]">Loading posts…</p> : null}

          {isError ? (
            <div className="home-card rounded-[28px] p-8 text-center">
              <p className="text-[#333333] leading-relaxed mb-4">We couldn&apos;t load the blog right now.</p>
              <button
                type="button"
                onClick={() => void refetch()}
                className="btn-cta rounded-full font-medium px-5 py-2.5 text-sm"
              >
                Try again
              </button>
            </div>
          ) : null}

          {!isLoading && !isError && (data?.data.length ?? 0) === 0 ? (
            <p className="text-[#666666] leading-relaxed">No posts yet — check back soon.</p>
          ) : null}

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-7 lg:gap-8">
            {data?.data.map((post) => (
              <PostCard key={post.slug} post={post} />
            ))}
          </div>

          {(data?.meta.last_page ?? 1) > 1 ? (
            <nav className="flex justify-center gap-2 mt-16" aria-label="Pagination">
              {Array.from({ length: data!.meta.last_page }, (_, index) => {
                const pageNumber = index + 1;
                const search = new URLSearchParams();
                if (tag) search.set("tag", tag);
                if (pageNumber > 1) search.set("page", String(pageNumber));
                const href = `/blog${search.toString() ? `?${search}` : ""}`;
                return (
                  <Link
                    key={pageNumber}
                    href={href}
                    aria-current={pageNumber === page ? "page" : undefined}
                    className={`min-w-[40px] h-10 inline-flex items-center justify-center rounded-full text-sm ${
                      pageNumber === page
                        ? "bg-[#D4FF00] text-[#061525] font-medium"
                        : "border border-[rgba(17,17,17,0.15)] text-[#666666] hover:text-[#061525] hover:bg-white/60"
                    }`}
                  >
                    {pageNumber}
                  </Link>
                );
              })}
            </nav>
          ) : null}
        </section>
      </PowloLayout>
    </>
  );
}
